import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { UtensilsCrossed, MessageCircle, Repeat2, Share, MoreHorizontal, Trash2, Sparkles, Lock, Send, Volume2, VolumeX, Flag, BarChart3, ShieldAlert } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { usePosts } from '@/hooks/usePosts';
import { REACTIONS, SKIN_TONES, getSkinTone, setSkinTone, getReactionEmoji } from '@/hooks/usePostActions';
import { ENERGIES, POST_BACKGROUNDS, CAMERA_FILTERS, ENERGY_DUOTONE } from '@/components/ComposerModal';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ComposerModal } from '@/components/ComposerModal';
import { ReportModal } from '@/components/ReportModal';
import { AccountabilityModal } from '@/components/AccountabilityModal';
import { LinkPreviewCard } from '@/components/TrendingWidget';
import { toast } from 'sonner';
import axios from 'axios';
import { cn } from '@/lib/utils';
import { getTopBadge } from '@/lib/badges';
import { asPlainText, BONITA_FEED_PLACEHOLDER, isBonitaFailurePlainText } from '@/lib/safeText';

// Theme hook
const useTheme = () => {
  const [isDark, setIsDark] = useState(() => {
    return document.documentElement.getAttribute('data-theme') === 'dark';
  });
  
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.getAttribute('data-theme') === 'dark');
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);
  
  return isDark;
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PostCard = ({ post, showThread = false, onBonitaContext, onLiveDrop }) => {
  // Safety check FIRST - before any hooks
  // If post is invalid, render a placeholder instead of crashing
  const isValidPost = post && post.post_id;

  const navigate = useNavigate();
  const { user } = useAuth();
  const { likePost, unlikePost, deletePost, checkLiked, votePoll, reactToPost, removeReaction } = usePosts();
  const feedProvidedLiked = isValidPost && post.user_liked !== undefined && post.user_liked !== null;
  const [isPlated, setIsPlated] = useState(() =>
    isValidPost ? Boolean(post.user_liked) : false
  );
  const [plateCount, setPlateCount] = useState(isValidPost ? (post.like_count || 0) : 0);
  const [replyOpen, setReplyOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [checkingLike, setCheckingLike] = useState(!feedProvidedLiked);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [accountabilityOpen, setAccountabilityOpen] = useState(false);
  const [pollData, setPollData] = useState(isValidPost ? post.poll : null);
  const [votedIndex, setVotedIndex] = useState(null);
  const [voting, setVoting] = useState(false);
  const [userReaction, setUserReaction] = useState(isValidPost ? (post.user_reaction || null) : null);
  const [reactionCounts, setReactionCounts] = useState(isValidPost ? (post.reaction_counts || {}) : {});
  const [reactionPickerOpen, setReactionPickerOpen] = useState(false);
  const [skinTone, setSkinToneState] = useState(() => getSkinTone());
  const [isPraised, setIsPraised] = useState(isValidPost ? (post.user_praised || false) : false);
  const [praiseCount, setPraiseCount] = useState(isValidPost ? (post.praise_count || 0) : 0);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const isDark = useTheme();

  // Theme-aware classes
  const textClass = isDark ? 'text-white' : 'text-gray-900';
  const textMutedClass = isDark ? 'text-white/50' : 'text-gray-500';
  const textVeryMutedClass = isDark ? 'text-white/30' : 'text-gray-400';
  const borderClass = isDark ? 'border-white/10' : 'border-gray-200';
  const borderLightClass = isDark ? 'border-white/20' : 'border-gray-300';
  const hoverBgClass = isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100';
  const avatarFallbackClass = isDark ? 'bg-white/10' : 'bg-gray-100';

  // Ensure post.user has default values (computed, not a hook)
  const postUser = isValidPost ? {
    name: post.user?.name || 'Anonymous',
    username: post.user?.username || 'user',
    picture: post.user?.picture || `https://api.dicebear.com/7.x/initials/svg?seed=${post.user?.name || 'U'}&backgroundColor=1a1a1a&textColor=ffffff`,
    user_id: post.user?.user_id || post.user_id,
    ...post.user
  } : { name: 'Unknown', username: 'user', picture: '', user_id: '' };

  // Auto-play video when visible in viewport
  useEffect(() => {
    if (!isValidPost || post.media_type !== 'video' || !videoRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            videoRef.current?.play().catch(() => {});
            setIsPlaying(true);
          } else {
            videoRef.current?.pause();
            setIsPlaying(false);
          }
        });
      },
      { threshold: 0.5 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [isValidPost, post.media_type]);

  const toggleMute = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  // Check plated state — feed may include user_liked to avoid N+1 /liked calls
  useEffect(() => {
    if (!isValidPost) return;
    if (feedProvidedLiked) {
      setIsPlated(Boolean(post.user_liked));
      setCheckingLike(false);
      return;
    }
    const check = async () => {
      try {
        const plated = await checkLiked(post.post_id);
        setIsPlated(plated);
      } catch (e) {
        // Ignore
      } finally {
        setCheckingLike(false);
      }
    };
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post?.post_id, post?.user_liked, feedProvidedLiked]);

  const handlePlate = async (e) => {
    e.stopPropagation();
    if (!isValidPost || checkingLike) return;
    
    try {
      if (isPlated) {
        await unlikePost(post.post_id);
        setPlateCount(prev => prev - 1);
        setIsPlated(false);
      } else {
        await likePost(post.post_id);
        setPlateCount(prev => prev + 1);
        setIsPlated(true);
        toast.success('Plate served! 🍽️');
      }
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const handlePraise = async (e) => {
    e.stopPropagation();
    if (!isValidPost) return;
    try {
      if (isPraised) {
        const res = await axios.delete(`${API}/posts/${post.post_id}/praise`, { withCredentials: true });
        setPraiseCount(res.data.praise_count);
        setIsPraised(false);
      } else {
        const res = await axios.post(`${API}/posts/${post.post_id}/praise`, {}, { withCredentials: true });
        setPraiseCount(res.data.praise_count);
        setIsPraised(true);
        toast("Let 'em cook!", { icon: '\u{1F373}' });
      }
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Action failed';
      toast.error(msg);
    }
  };

  const handleDelete = async () => {
    try {
      await deletePost(post.post_id);
      toast.success('Post deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/post/${post.post_id}`);
      toast.success('Link copied!');
    } catch (e) {
      toast.error('Failed to copy');
    }
  };

  const handleReact = async (reactionKey) => {
    if (!isValidPost) return;
    try {
      let result;
      if (userReaction === reactionKey) {
        result = await removeReaction(post.post_id);
        setUserReaction(null);
      } else {
        result = await reactToPost(post.post_id, reactionKey);
        setUserReaction(reactionKey);
      }
      setReactionCounts(result.reaction_counts || {});
    } catch {
      toast.error('Reaction failed');
    }
  };

  const handleLiveDrop = () => {
    if (onLiveDrop) {
      onLiveDrop(post);
      toast.success('Dropped to The GC!');
    }
  };

  // Check if user already voted on this poll
  useEffect(() => {
    if (!isValidPost || !post.poll) return;
    if (user && post.poll.voters?.includes(user.user_id)) {
      // Find which option they voted for (we don't store this, so just mark as voted)
      setVotedIndex(-1); // -1 = voted but we don't know which (show results)
    }
  }, [isValidPost, post?.poll, user]);

  const handleVote = async (optionIndex) => {
    if (!isValidPost || voting || votedIndex !== null) return;
    setVoting(true);
    try {
      const result = await votePoll(post.post_id, optionIndex);
      setPollData(result.poll);
      setVotedIndex(result.voted);
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Vote failed';
      toast.error(msg);
    } finally {
      setVoting(false);
    }
  };

  const energyInfo = isValidPost && post.energy
    ? ENERGIES.find(e => e.key === post.energy)
    : null;

  // Map energy label → CSS pill class
  const getEnergyPillClass = () => {
    if (!energyInfo) return '';
    const label = (energyInfo.label || '').toLowerCase();
    if (label.includes('hot'))     return 'ep-hot';
    if (label.includes('real'))    return 'ep-real';
    if (label.includes('confess')) return 'ep-con';
    if (label.includes('plug'))    return 'ep-plug';
    if (label === 'w')             return 'ep-w';
    return '';
  };
  const energyPillClass = getEnergyPillClass();
  const isBonita = post.is_spark || post.user_id === 'bonita' || postUser.username === 'bonita';
  // Large italic body for confession-energy or short posts
  const rawBody = asPlainText(post.content);
  const bodyText =
    isBonita && isBonitaFailurePlainText(post.content) ? BONITA_FEED_PLACEHOLDER : rawBody;
  const useBigBody = energyPillClass === 'ep-con' || (!post.media_url && bodyText.length < 180);

  // Visual enhancements
  const bgInfo = post?.background ? POST_BACKGROUNDS.find(b => b.key === post.background) : null;
  const filterInfo = post?.filter ? CAMERA_FILTERS.find(f => f.key === post.filter) : null;
  const duotone = post?.media_url && post?.energy ? ENERGY_DUOTONE[post.energy] : null;

  const formatTime = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return formatDistanceToNow(date, { addSuffix: false });
    } catch {
      return '';
    }
  };

  const isOwner = user?.user_id === post?.user_id;
  const isCookout = post?.visibility === 'cookout';

  // Now we can safely return null for invalid posts (after all hooks)
  if (!isValidPost) {
    console.warn('PostCard: Invalid post data', post);
    return null;
  }

  return (
    <>
      <article
        className={cn(
          "card",
          post.reply_count > 0 && "threaded",
          isBonita && "bonita-card"
        )}
        onClick={() => navigate(`/post/${post.post_id}`)}
        data-testid={`post-${post.post_id}`}
      >
        {/* Subtle background gradient tint for text posts */}
        {bgInfo && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: bgInfo.css, opacity: 0.12 }}
          />
        )}
        {/* Energy emoji watermark */}
        {energyInfo && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-end overflow-hidden">
            <span className="select-none mr-4" style={{ fontSize: '7rem', opacity: bgInfo ? 0.1 : 0.06 }}>
              {energyInfo.icon}
            </span>
          </div>
        )}

        {/* Energy pill — top of card */}
        {energyInfo && energyPillClass && (
          <span className={cn("ep", energyPillClass)}>
            <span>{energyInfo.icon}</span>
            {energyInfo.label}
          </span>
        )}

        {/* Card header — avatar + user info */}
        <div className="ch">
          <Link
            to={`/profile/${postUser.username}`}
            onClick={(e) => e.stopPropagation()}
            className="flex-shrink-0"
          >
            <Avatar className="av h-11 w-11">
              <AvatarImage src={postUser.picture} alt={postUser.name} />
              <AvatarFallback className={cn(avatarFallbackClass, "text-base font-medium")}>
                {postUser.name?.charAt(0)?.toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
          </Link>

          <div className="cu">
            <div className="flex items-center justify-between">
              <div className="card-name">
                <Link
                  to={`/profile/${postUser.username}`}
                  onClick={(e) => e.stopPropagation()}
                  className="hover:underline"
                >
                  {postUser.name}
                </Link>
                {postUser.is_verified && <span className="vtick">&#10003;</span>}
                {isBonita && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 text-[10px] font-medium text-purple-400 rounded-sm">
                    <Sparkles className="h-2.5 w-2.5" />
                    AI
                  </span>
                )}
                {(post.metadata?.source === 'hbcu_news' || postUser.press_tier === 'verified_press' || postUser.press_tier === 'campus_reporter') && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', padding: '1px 5px', background: 'rgba(240,123,29,0.12)', border: '1px solid rgba(240,123,29,0.3)', borderRadius: '3px', fontSize: '9px', letterSpacing: '1px', color: 'var(--fire)', fontFamily: "'Azeret Mono', monospace" }}>
                    🎙 HBCU.news
                  </span>
                )}
                {postUser.school && <span className="stag">{postUser.school}</span>}
                {(() => {
                  const badge = getTopBadge(postUser.active_badges);
                  return badge ? (
                    <span className={cn("inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-medium rounded-sm", badge.bg, badge.color)} title={badge.label}>
                      {badge.emoji} {badge.label}
                    </span>
                  ) : null;
                })()}
                {isCookout && <Lock className="h-3.5 w-3.5 text-[#72788A]" />}
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn("h-8 w-8", textVeryMutedClass, isDark ? "hover:text-white" : "hover:text-gray-900")}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-card border-border">
                  {onBonitaContext && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onBonitaContext(bodyText); }} className={cn("cursor-pointer text-xs", isDark ? "text-white/70 hover:text-white focus:text-white" : "text-gray-600 hover:text-gray-900 focus:text-gray-900")}>
                      <Sparkles className="h-3.5 w-3.5 mr-2" />
                      Ask Bonita
                    </DropdownMenuItem>
                  )}
                  {onLiveDrop && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleLiveDrop(); }} className={cn("cursor-pointer text-xs", isDark ? "text-white/70 hover:text-white focus:text-white" : "text-gray-600 hover:text-gray-900 focus:text-gray-900")}>
                      <Send className="h-3.5 w-3.5 mr-2" />
                      Drop to GC
                    </DropdownMenuItem>
                  )}
                  {isOwner && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDelete(); }} className="text-red-500 hover:text-red-400 focus:text-red-400 cursor-pointer text-xs">
                      <Trash2 className="h-3.5 w-3.5 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  )}
                  {!isOwner && user && isCookout && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setAccountabilityOpen(true); }} className="cursor-pointer text-xs text-red-400 hover:text-red-300 focus:text-red-300">
                      <ShieldAlert className="h-3.5 w-3.5 mr-2" />
                      Come Get Your Mans
                    </DropdownMenuItem>
                  )}
                  {!isOwner && user && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setReportOpen(true); }} className={cn("cursor-pointer text-xs", isDark ? "text-white/70 hover:text-white focus:text-white" : "text-gray-600 hover:text-gray-900 focus:text-gray-900")}>
                      <Flag className="h-3.5 w-3.5 mr-2" />
                      Report
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="cmeta">@{postUser.username} · {formatTime(post.created_at)}</div>
          </div>
        </div>

        {/* Reply indicator */}
        {post.post_type === 'reply' && post.parent_post && (
          <>
            <div className="rt-label">
              ↩ Replying to <span className="rname">@{post.parent_post.user?.username}</span>
            </div>
            {asPlainText(post.parent_post.content) && (
              <div className="rt-ctx">
                {(() => {
                  const pc = asPlainText(post.parent_post.content);
                  return pc.length > 120 ? `${pc.slice(0, 120)}...` : pc;
                })()}
              </div>
            )}
          </>
        )}

        {/* Post body */}
        <div className={cn("cb", useBigBody && "big")}>
          {bodyText}
        </div>

        {/* Media */}
        {post.media_url && (
          <div ref={containerRef} className="media-wrap">
            {post.media_type === 'video' ? (
              <div className="relative">
                <video
                  ref={videoRef}
                  src={post.media_url}
                  loop muted playsInline preload="metadata"
                  className="w-full max-h-[500px] object-contain bg-black cursor-pointer"
                  style={filterInfo && filterInfo.css !== 'none' ? { filter: filterInfo.css } : undefined}
                  onClick={(e) => { e.stopPropagation(); videoRef.current?.paused ? videoRef.current.play() : videoRef.current?.pause(); }}
                />
                <div className="absolute top-2 left-2 bg-amber-500 px-2 py-1 text-[10px] text-black font-bold font-display tracking-wider rounded">POV</div>
                <button onClick={toggleMute} className="absolute bottom-3 right-3 bg-black/70 hover:bg-black/90 p-2 rounded-full transition-colors">
                  {isMuted ? <VolumeX className="h-4 w-4 text-white" /> : <Volume2 className="h-4 w-4 text-white" />}
                </button>
              </div>
            ) : (
              <div className="relative">
                <img
                  src={post.media_url} alt=""
                  className="w-full max-h-[500px] object-contain bg-black"
                  loading="lazy"
                  style={filterInfo && filterInfo.css !== 'none' ? { filter: filterInfo.css } : undefined}
                />
                {duotone && !post.filter && (
                  <div className="absolute inset-0 pointer-events-none" style={{ background: `linear-gradient(135deg, ${duotone.from}, ${duotone.to})`, mixBlendMode: 'color', opacity: 0.55 }} />
                )}
                {post.metadata?.source === 'the_lab' && (
                  <div style={{ position: 'absolute', top: '8px', right: '10px', fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '2px', color: 'rgba(255,255,255,.3)' }}>
                    THE LAB
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Rich Link Preview */}
        {post.reference_url && (
          <div className="mb-3" onClick={(e) => e.stopPropagation()}>
            <LinkPreviewCard url={post.reference_url} />
          </div>
        )}

        {/* Poll */}
        {pollData && (
          <div className="mb-3 space-y-1.5" onClick={(e) => e.stopPropagation()}>
            {pollData.options.map((option, index) => {
              const hasVoted = votedIndex !== null;
              const totalVotes = pollData.total_votes || 0;
              const pct = totalVotes > 0 ? Math.round((option.votes / totalVotes) * 100) : 0;
              const isMyVote = votedIndex === index;
              return hasVoted ? (
                <div key={index} className={cn("relative border px-3 py-2 overflow-hidden", isMyVote ? (isDark ? "border-amber-500/40" : "border-amber-500/50") : (isDark ? "border-white/10" : "border-gray-200"))}>
                  <div className={cn("absolute inset-y-0 left-0 transition-all duration-500", isMyVote ? (isDark ? "bg-amber-500/15" : "bg-amber-500/10") : (isDark ? "bg-white/5" : "bg-gray-100"))} style={{ width: `${pct}%` }} />
                  <div className="relative flex items-center justify-between">
                    <span className={cn("text-sm", isMyVote ? (isDark ? "text-amber-400 font-medium" : "text-amber-600 font-medium") : textClass)}>{option.text}</span>
                    <span className={cn("text-xs font-mono ml-2", textMutedClass)}>{pct}%</span>
                  </div>
                </div>
              ) : (
                <button key={index} onClick={() => handleVote(index)} disabled={voting} className={cn("w-full text-left border px-3 py-2 text-sm transition-colors", isDark ? "border-white/10 hover:border-white/25 hover:bg-white/5" : "border-gray-200 hover:border-gray-400 hover:bg-gray-50", textClass, voting && "opacity-50 cursor-wait")}>
                  {option.text}
                </button>
              );
            })}
            <div className={cn("flex items-center gap-2 pt-1", textVeryMutedClass)}>
              <BarChart3 className="h-3 w-3" />
              <span className="text-[11px]">{pollData.total_votes} {pollData.total_votes === 1 ? 'vote' : 'votes'}</span>
            </div>
          </div>
        )}

        {/* Quoted post */}
        {post.quote_post && (
          <div className={cn("border p-3 mb-3 transition-colors", borderLightClass, hoverBgClass)} onClick={(e) => { e.stopPropagation(); navigate(`/post/${post.quote_post.post_id}`); }}>
            <div className="flex items-center gap-2 mb-2">
              <Avatar className="h-5 w-5">
                <AvatarImage src={post.quote_post.user?.picture} />
                <AvatarFallback className="text-[10px]">{post.quote_post.user?.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <span className={cn("text-xs font-medium", isDark ? "text-white/70" : "text-gray-700")}>{post.quote_post.user?.name}</span>
              <span className={cn("text-[10px]", textVeryMutedClass)}>@{post.quote_post.user?.username}</span>
            </div>
            <p className={cn("text-sm line-clamp-3", isDark ? "text-white/60" : "text-gray-600")}>{asPlainText(post.quote_post.content)}</p>
          </div>
        )}

        {/* Actions */}
        <div className="acts" onClick={(e) => e.stopPropagation()}>
          <button className={cn("act-btn act-plate", isPlated && "act-plated")} onClick={handlePlate} title="Serve a Plate" data-testid={`post-${post.post_id}-plate`}>
            <UtensilsCrossed className="h-[14px] w-[14px]" />
            {plateCount > 0 && <span className="act-n">{plateCount}</span>}
          </button>
          <button className="act-btn act-reply" onClick={(e) => { e.stopPropagation(); setReplyOpen(true); }} data-testid={`post-${post.post_id}-reply`}>
            <MessageCircle className="h-[14px] w-[14px]" />
            {post.reply_count > 0 && <span className="act-n">{post.reply_count}</span>}
          </button>
          <button className="act-btn act-repost" onClick={(e) => { e.stopPropagation(); setQuoteOpen(true); }} data-testid={`post-${post.post_id}-repost`}>
            <Repeat2 className="h-[14px] w-[14px]" />
            {post.repost_count > 0 && <span className="act-n">{post.repost_count}</span>}
          </button>
          {isCookout && !isOwner && user && (
            <button className={cn("act-btn act-praise", isPraised && "act-praised")} onClick={handlePraise} title="Let 'Em Cook">
              <span>🍳</span>
              {praiseCount > 0 && <span className="act-n">{praiseCount}</span>}
            </button>
          )}
          <div className="act-gap" />
          <button className="act-btn" onClick={(e) => { e.stopPropagation(); handleShare(); }} data-testid={`post-${post.post_id}-share`}>
            <Share className="h-[14px] w-[14px]" />
          </button>
          <div className="relative">
            <button className="act-btn text-base" onClick={(e) => { e.stopPropagation(); setReactionPickerOpen(!reactionPickerOpen); }} title="React">
              {userReaction ? getReactionEmoji(REACTIONS.find(r => r.key === userReaction), skinTone) : getReactionEmoji(REACTIONS.find(r => r.key === 'real_talk'), skinTone)}
            </button>
            {reactionPickerOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setReactionPickerOpen(false)} />
                <div className={cn("absolute bottom-9 right-0 z-50 border shadow-xl", isDark ? "bg-zinc-900 border-white/10" : "bg-white border-gray-200")}>
                  <div className={cn("flex items-center gap-0.5 px-2 py-1 border-b", isDark ? "border-white/10" : "border-gray-100")}>
                    {SKIN_TONES.map(tone => (
                      <button key={tone.key} title={tone.key} onClick={(e) => { e.stopPropagation(); setSkinTone(tone.modifier); setSkinToneState(tone.modifier); }} className={cn("w-7 h-7 flex items-center justify-center rounded-full text-sm transition-all hover:scale-110", skinTone === tone.modifier && "bg-amber-500/20 ring-1 ring-amber-500/50")}>
                        {'✊' + tone.modifier}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-0.5 px-2 py-1">
                    {REACTIONS.map(r => (
                      <button key={r.key} title={r.label} onClick={(e) => { e.stopPropagation(); handleReact(r.key); setReactionPickerOpen(false); }} className={cn("text-lg w-8 h-8 flex items-center justify-center rounded-full transition-all hover:scale-125", userReaction === r.key && "bg-amber-500/20")}>
                        {getReactionEmoji(r, skinTone)}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Thread indicator */}
        {post.reply_count > 0 && (
          <div className="thread-row" onClick={(e) => { e.stopPropagation(); navigate(`/post/${post.post_id}`); }} data-testid={`post-${post.post_id}-view-replies`}>
            <div className="tdots">
              <div className="td top" /><div className="td" /><div className="td" />
            </div>
            <span className="thread-count">{post.reply_count} {post.reply_count === 1 ? 'reply' : 'replies'} in thread</span>
            <span className="thread-arrow">→</span>
          </div>
        )}

        {/* Reaction counts shelf */}
        {Object.keys(reactionCounts).length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap mt-2 -ml-1">
            {REACTIONS.filter(r => reactionCounts[r.key] > 0).map(r => (
              <button key={r.key} onClick={(e) => { e.stopPropagation(); handleReact(r.key); }} className={cn("flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition-all", userReaction === r.key ? "bg-amber-500/15 border-amber-500/40 text-amber-400" : isDark ? "bg-white/5 border-white/10 text-white/60 hover:bg-amber-500/10 hover:border-amber-500/30" : "bg-gray-50 border-gray-200 text-gray-500 hover:bg-amber-50 hover:border-amber-200")}>
                <span>{getReactionEmoji(r, skinTone)}</span>
                <span className="font-mono">{reactionCounts[r.key]}</span>
              </button>
            ))}
          </div>
        )}
      </article>

      {/* Reply Modal */}
      <ComposerModal 
        open={replyOpen} 
        onOpenChange={setReplyOpen} 
        replyTo={post}
      />

      {/* Quote Modal */}
      <ComposerModal 
        open={quoteOpen} 
        onOpenChange={setQuoteOpen} 
        quotedPost={post}
      />

      {/* Report Modal */}
      <ReportModal
        open={reportOpen}
        onOpenChange={setReportOpen}
        targetType="post"
        targetId={post.post_id}
        targetName={`@${postUser.username}'s post`}
      />
      {/* Accountability Modal */}
      <AccountabilityModal
        open={accountabilityOpen}
        onOpenChange={setAccountabilityOpen}
        targetUserId={post.user_id}
        targetPostId={post.post_id}
        targetName={`@${postUser.username}`}
      />
    </>
  );
};
